import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const statusPillVariants = cva(
	"inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium tracking-tight rounded-md border transition-colors duration-150",
	{
		variants: {
			tone: {
				ready:
					"text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/[0.08] border-emerald-200/80 dark:border-emerald-400/20",
				loading:
					"text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-500/[0.08] border-sky-200/80 dark:border-sky-400/20",
				error:
					"text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/[0.08] border-red-200/80 dark:border-red-400/20",
				debugging:
					"text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/[0.08] border-orange-200/80 dark:border-orange-400/20",
				finished:
					"text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/[0.08] border-indigo-200/80 dark:border-indigo-400/20",
				neutral:
					"border-gray-200 dark:border-white/[0.08] text-gray-600 dark:text-gray-400 bg-gray-50/50 dark:bg-white/[0.02]",
			},
		},
		defaultVariants: {
			tone: "neutral",
		},
	},
);

const dotColorByTone: Record<
	NonNullable<VariantProps<typeof statusPillVariants>["tone"]>,
	string
> = {
	ready: "bg-emerald-500",
	loading: "bg-sky-500",
	debugging: "bg-orange-500",
	error: "bg-red-500",
	finished: "bg-indigo-500",
	neutral: "bg-gray-400",
};

export interface StatusPillProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof statusPillVariants> {
	label?: React.ReactNode;
	pulse?: boolean;
}

const StatusPill = React.forwardRef<HTMLDivElement, StatusPillProps>(
	({ className, tone = "neutral", label, pulse, children, ...props }, ref) => {
		const safeTone = tone ?? "neutral";
		const dotColor = dotColorByTone[safeTone];
		return (
			<div
				ref={ref}
				className={cn(statusPillVariants({ tone, className }))}
				{...props}
			>
				<span className={cn("relative inline-flex h-1.5 w-1.5 rounded-full", dotColor)}>
					{pulse && (
						<span
							className={cn(
								"absolute inset-0 inline-flex h-full w-full rounded-full opacity-60 animate-ping",
								dotColor,
							)}
						/>
					)}
				</span>
				{label && <span className="tabular tracking-tight">{label}</span>}
				{children}
			</div>
		);
	},
);
StatusPill.displayName = "StatusPill";

export { StatusPill, statusPillVariants };
